import React, { useState, useEffect } from 'react'
import View from './View'
import {
  getStartEndDateFromWeekFilter,
  SORT_OPTIONS,
} from '../../../../../Utils/Table'
import { getMissionsByCity } from './graphql'
import {
  browserSafeNewDate,
  dropTimeZoneFromDate,
  get24HourTime,
  getWeekNumber,
  toLocaleNonAmericanDateString,
} from '../../../../../shared/libraries/utils/date'
import { StatusLabel } from '../../../../../Components/StatusLabel'
import { calculateTimeOfDay } from 'Utils/Mission/index'
import {
  MISSION_STATUS_ENUM,
  MISSION_STATUS_SPIED,
  MISSION_STATUS_SPYING,
} from '../../../../../shared/constants'
import { urlAdd, urlGet, urlRemove } from '../../../../../Utils/URL'

const Roster = props => {
  const { cityId } = props
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({
    dayPeriod: urlGet('Day Period'),
    status: urlGet('Status'),
    week: urlGet('Week'),
  })
  const [orderBy, setOrderBy] = useState({})
  const [data, setData] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [offset, setOffset] = useState(0)
  const [resultsPerPage, setResultsPerPage] = useState(50)

  // because we use searchInput in list and roster components
  // we need to remove tableSearch url param to prevent using
  // search string from list component
  useEffect(() => {
    urlRemove('tableSearch')
    return () => {
      urlRemove('tableSearch')
    }
  }, [])

  useEffect(() => {
    fetchData(cityId, filters)
  }, [search, filters, orderBy, offset, resultsPerPage])

  useEffect(() => {
    if (filters && filters.dayPeriod) {
      setOffset(0)
    }
  }, [filters])

  const convertFilters = () => {
    const convertedFilters = {
      canBeSpied: true,
      timeOfDay: filters['dayPeriod'] ? filters['dayPeriod'] : null,
      spyUserId: undefined,
      hasAssignedSpy: undefined,
      spyCheckedIn: undefined,
      spyCheckedOut: undefined,
      reported: undefined,
    }

    if (filters.status === MISSION_STATUS_ENUM.AVAILABLE) {
      convertedFilters.spyUserId = null
      convertedFilters.statusAndAbove = MISSION_STATUS_ENUM.BOOKED
      convertedFilters.statusAndBelow = MISSION_STATUS_ENUM.BOOKED
    } else if (filters.status === MISSION_STATUS_ENUM.BOOKED) {
      convertedFilters.hasAssignedSpy = true
      convertedFilters.spyCheckedIn = false
      convertedFilters.spyCheckedOut = false
    } else if (filters.status === MISSION_STATUS_SPYING) {
      convertedFilters.spyCheckedIn = true
      convertedFilters.spyCheckedOut = false
    } else if (filters.status === MISSION_STATUS_SPIED) {
      convertedFilters.spyCheckedOut = true
      convertedFilters.reported = true
    }

    return convertedFilters
  }

  const fetchData = async (cityId, filters) => {
    const { startDate, endDate } = getStartEndDateFromWeekFilter(filters.week)

    setIsLoading(true)
    try {
      const response = await getMissionsByCity({
        // Map order by to supported order by values
        ...convertFilters(),
        first: resultsPerPage,
        skip: offset,
        orderBy: convertOrder(orderBy),
        startDate,
        endDate,
        cityId,
        searchWithSpy: search,
      })
      const result = await response.json()

      if (result['errorMessage']) throw new Error(result['errorMessage'])
      if (result.errors && result.errors[0])
        throw new Error(result.errors[0].message)

      setData(marshalData(result.data))
      setTotalCount(result.data.city.missions.totalCount)
    } catch (e) {
      setData([])
      setTotalCount(0)
      throw e
    } finally {
      setIsLoading(false)
    }
  }

  const marshalData = data => {
    const rows = data.city.missions.edges.map(o => {
      const node = o.node
      const localDateStr = dropTimeZoneFromDate(node.dateTime),
        localDate = browserSafeNewDate(localDateStr)

      const timeOfDay = calculateTimeOfDay(
        node.dateTime,
        data.city.lunchCheckInTime,
        data.city.afternoonCheckInTime,
      )

      const spy = node.spyInformation && node.spyInformation.spy
      return {
        Client: node.cityPlan.campaign.client.name,
        Week: 'W' + getWeekNumber(localDate),
        Date: toLocaleNonAmericanDateString(localDate),
        Time: get24HourTime(localDate),
        TOD: timeOfDay,
        Location: node.location ? node.location.name : '',
        BA: node.user ? node.user.firstName + ' ' + node.user.lastName : '',
        'Spy Name': spy ? (
          <div
            onClick={e => {
              e.stopPropagation()
              props.history.push(`/dashboard/cities/${cityId}/spies/${spy.id}`)
            }}
          >
            {spy.firstName} {spy.lastName}
          </div>
        ) : (
          ''
        ),
        Checkin: node.checkInTime
          ? get24HourTime(new Date(node.checkInTime))
          : null,
        Checkout: node.checkoutTime
          ? get24HourTime(new Date(node.checkoutTime))
          : null,
        Status: (
          <StatusLabel
            isSpied={
              node.spyInformation &&
              node.spyInformation.spyDetails &&
              node.spyInformation.spyDetails.checkInTime
            }
            status={node.status}
          />
        ),
        missionId: node.id,
      }
    })
    return rows
  }

  const convertOrder = ({ column, direction }) => {
    const convertedOrder = `${column}_${
      direction === 'DESCENDING' ? 'DESC' : 'ASC'
    }`.toUpperCase()

    return SORT_OPTIONS[convertedOrder]
  }

  const changeSort = value => {
    setOrderBy(value)
  }

  const onOffsetChange = value => {
    if (value < totalCount && value >= 0) {
      setOffset(value)
    }
  }

  const handleSearch = search => {
    urlAdd('tableSearch', search)
    setSearch(search)
  }

  return (
    <View
      data={data}
      handleSearch={handleSearch}
      changeSort={changeSort}
      setFilters={setFilters}
      search={search}
      filters={filters}
      orderBy={orderBy}
      isLoading={isLoading}
      onOffsetChange={onOffsetChange}
      resultsPerPage={resultsPerPage}
      setResultsPerPage={setResultsPerPage}
      totalCount={totalCount}
      offsetRecordsStart={offset}
      {...props}
    />
  )
}

export default Roster
